import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGaugeHigh,
  faLaptopCode,
  faRightFromBracket,
  faUserPlus,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../auth/AuthContext";
import { AppSidebarHeader } from "../layout/AppSidebarHeader";
import { usePersistedSidebarCollapsed } from "../layout/usePersistedSidebarCollapsed";
import { StudentCohortProvider, useStudentCohorts } from "./StudentCohortContext";
import { StudentSidebarCohortLink } from "./StudentSidebarCohortLink";
import { studentHelpEmail } from "./studentHelpEmail";
import { playgroundNavIcon } from "./playgroundNavIcon";

const NAV_ENROLLED = [
  { to: "/lessons", label: "Home", icon: faGaugeHigh, end: true },
  { to: "/playground", label: "Playgrounds", icon: playgroundNavIcon },
  { to: "/lessons/cohorts", label: "My cohorts", icon: faUsers },
] as const;

const NAV_UNENROLLED = [
  { to: "/lessons", label: "Home", icon: faGaugeHigh, end: true },
  { to: "/ide", label: "Demo IDE", icon: faLaptopCode },
  { to: "/join", label: "Join cohort", icon: faUserPlus },
] as const;

function StudentAppShellInner({ children }: { readonly children: ReactNode }) {
  const { identity, signOut } = useAuth();
  const location = useLocation();
  const { collapsed, toggleCollapsed } = usePersistedSidebarCollapsed("ccs-student-nav-collapsed");
  const { cohorts, hasCohorts, loading: cohortsLoading } = useStudentCohorts();
  const nav = hasCohorts ? NAV_ENROLLED : NAV_UNENROLLED;
  const navLoading = cohortsLoading && !hasCohorts;
  const helpEmail = hasCohorts ? studentHelpEmail(cohorts, location.pathname) : null;

  return (
    <div className={`student-app${collapsed ? " student-app--nav-collapsed" : ""}`}>
      <aside
        className={`student-sidebar${collapsed ? " is-collapsed" : ""}`}
        aria-label="Student navigation"
      >
        <AppSidebarHeader
          collapsed={collapsed}
          toggleCollapsed={toggleCollapsed}
          navId="student-sidebar-nav"
          workspaceLabel="Student Workspace"
          email={identity?.email ?? ""}
        />
        <nav id="student-sidebar-nav" className="student-sidebar-nav" aria-busy={navLoading || undefined}>
          {navLoading ? (
            <>
              {[0, 1, 2].map((i) => (
                <div key={i} className="student-sidebar-link student-sidebar-link--skeleton" aria-hidden="true">
                  <div className="skeleton skeleton--nav-icon" />
                  <div className="skeleton skeleton--nav-label" style={{ width: `${72 - i * 8}%` }} />
                </div>
              ))}
            </>
          ) : (
            <>
              {nav.map(({ to, label, icon, ...rest }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={"end" in rest ? rest.end : false}
                  className={({ isActive }) =>
                    `student-sidebar-link${isActive ? " student-sidebar-link--active" : ""}`
                  }
                  title={collapsed ? label : undefined}
                >
                  <FontAwesomeIcon icon={icon} fixedWidth />
                  <span className="student-nav-text app-sidebar-label">{label}</span>
                </NavLink>
              ))}
              {hasCohorts && cohorts.length > 0 ? (
                <div className="student-sidebar-cohorts">
                  {!collapsed ? (
                    <span className="student-sidebar-section-label app-sidebar-label">Cohorts</span>
                  ) : null}
                  <div className="student-sidebar-cohort-list">
                    {cohorts.map((cohort) => (
                      <StudentSidebarCohortLink key={cohort.cohortId} cohort={cohort} collapsed={collapsed} />
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </nav>
        <div className="app-sidebar-footer student-sidebar-footer">
          {helpEmail ? (
            <p className="app-sidebar-support student-sidebar-support">
              Need help?{" "}
              <a href={`mailto:${helpEmail}`}>{helpEmail}</a>
            </p>
          ) : null}
          <button
            type="button"
            className="student-sidebar-signout btn secondary"
            onClick={() => signOut()}
            title={collapsed ? "Sign out" : undefined}
          >
            <FontAwesomeIcon icon={faRightFromBracket} fixedWidth />
            <span className="student-nav-text app-sidebar-label">Sign out</span>
          </button>
        </div>
      </aside>
      <div className="student-main" key={location.pathname}>
        {children}
      </div>
    </div>
  );
}

export function StudentAppShell({ children }: { readonly children: ReactNode }) {
  return (
    <StudentCohortProvider>
      <StudentAppShellInner>{children}</StudentAppShellInner>
    </StudentCohortProvider>
  );
}
